import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { COOKIE_SELECTED_TEAM } from '@constants/cookies';
import {
  COMPANY_INFO_ROUTE,
  ORGANIZER_INVITATION_ROUTE,
  SELECT_VENUE_ROUTE,
  USER_SETTINGS_ROUTE,
} from '@constants/routes';
import { useGetAuthContext } from '@hooks/auth/use-get-auth-context';
import { useGetCompanyClub } from '@hooks/company-club/use-get-company-club';
import { useSetProfiler } from '@hooks/profiler/use-set-profiler';
import { usePostHog } from '@posthog/react';
import { identifyCompanyContext } from '@posthog-app/company-identification';
import { getVenueFromPath } from '@utils/get-venue-from-path/get-venue-from-path';
import Cookies from 'js-cookie';

import { getProfilerGroups } from './utils/get-profiler-groups';
import { CompanyAuthContext } from './company-auth-context';
import { CompanyAuthContextValue } from './company-auth-context.type';

export const CompanyAuthContextProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { companyId, clubSlug } = getVenueFromPath(pathname);
  const [companyAuthId, setCompanyAuthId] = useState('');
  const [companyClubId, setCompanyClubId] = useState('');
  const posthog = usePostHog();
  const isClubIdOptional = useMatch(COMPANY_INFO_ROUTE);
  // Disabled: CompanyClubProvider fetches it, this only reads the venue the URL points at.
  const { data: urlCompanyClub } = useGetCompanyClub({ companyId, clubSlug, options: { enabled: false } });
  // The ids are set when a venue is picked and when CompanyClubProvider resolves the URL, so right
  // after a navigation, Back included, they can still be the previous venue's.
  const isUrlVenueContext =
    !!urlCompanyClub && urlCompanyClub.company.id === companyAuthId && urlCompanyClub.club.id === companyClubId;
  const {
    data: companyAuthResponse,
    refetch,
    isFetching,
    isSuccess,
    isError,
  } = useGetAuthContext({
    companyId: companyAuthId,
    clubId: isClubIdOptional ? undefined : companyClubId,
    options: { enabled: !!companyAuthId && (!!isClubIdOptional || !!companyClubId) },
  });
  const { mutate: setProfiler } = useSetProfiler();

  const unprotectedRoutes = [SELECT_VENUE_ROUTE, USER_SETTINGS_ROUTE, ORGANIZER_INVITATION_ROUTE];

  useEffect(() => {
    if (
      isUrlVenueContext &&
      companyAuthResponse &&
      !isFetching &&
      !unprotectedRoutes.some((route) => pathname.includes(route))
    )
      refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!companyAuthResponse) return;
    const isOutsideCompany = unprotectedRoutes.some((route) => pathname.includes(route));
    if (!isOutsideCompany && !isUrlVenueContext) return;
    const groups = getProfilerGroups(companyAuthResponse, {
      companyId,
      isOutsideCompany,
      selectedTeamId: Cookies.get(COOKIE_SELECTED_TEAM),
    });
    for (const group of groups) setProfiler(group);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyAuthResponse, setProfiler, companyId, isUrlVenueContext]);

  // Super-admin sessions stay out of company analytics so internal activity doesn't
  // pollute customer segmentation. Impersonated sessions do run this: the company group
  // and super properties are what company-scoped feature flags resolve against, and
  // capture is off for the whole session, so nothing is sent.
  // The auth/context response only carries the company UUID, so the legacy int id —
  // the one used by feature-flag payloads and the profiler — comes from the URL,
  // same as the profiler effect above. NaN on non-venue routes skips the call.
  useEffect(() => {
    const legacyCompanyId = Number.parseInt(companyId);
    if (Number.isNaN(legacyCompanyId)) return;
    if (!companyAuthResponse || !isUrlVenueContext || !posthog || companyAuthResponse.user.isSuperAdmin) return;
    identifyCompanyContext(posthog, {
      userId: companyAuthResponse.user.id,
      companyId: legacyCompanyId,
      role: companyAuthResponse.role.name,
    });
  }, [companyAuthResponse, isUrlVenueContext, posthog, companyId]);

  const memoizedContextValue = useMemo<CompanyAuthContextValue>(
    () => ({
      isFinished: isUrlVenueContext && (isSuccess || isError),
      permissions: isUrlVenueContext ? companyAuthResponse?.permissions : [],
      role: isUrlVenueContext ? companyAuthResponse?.role : undefined,
      channels: isUrlVenueContext ? companyAuthResponse?.channels : [],
      teams: isUrlVenueContext ? companyAuthResponse?.teams : [],
      setCompanyAuthId,
      setCompanyClubId,
    }),
    [companyAuthResponse, isUrlVenueContext, isSuccess, isError]
  );

  return <CompanyAuthContext.Provider value={memoizedContextValue}>{children}</CompanyAuthContext.Provider>;
};
